import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Stack, Tooltip, Chip, Paper, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText,
  OutlinedInput,
} from '@mui/material';
import {
  Add as AddIcon, RefreshOutlined, ContentCopyOutlined, BlockOutlined,
  DeleteOutlined, OpenInNewOutlined, CloseOutlined, WarningAmberOutlined,
  LockOutlined,
} from '@mui/icons-material';
import { apiKeysApi, type ApiKeyResponse, type ApiKeyCreatedResponse } from '../../api/integrations';
import { PlanFeatureBanner } from '../../components/integrations/PlanFeatureBanner';
import { useApiAccessLevel } from '../../hooks/useApiAccessLevel';
import { useTranslation } from 'react-i18next';
import { activeLocale } from '../../utils/datetime';

type Toast = { message: string; severity: 'success' | 'error' | 'info' } | null;

const AVAILABLE_SCOPES = [
  'attendance:read', 'attendance:write', 'workers:read', 'workers:write',
  'processes:read', 'processes:write', 'organization:read', 'system:read',
  'integrations:read', 'integrations:write',
];

const EXPIRY_OPTIONS = [
  { value: '', i18nKey: 'integrations:api.neverExpires' },
  { value: '90', i18nKey: 'integrations:api.days90' },
  { value: '365', i18nKey: 'integrations:api.year1' },
];

const API: React.FC = () => {
  const { t } = useTranslation();
  const [keys, setKeys] = useState<ApiKeyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [newExpiry, setNewExpiry] = useState('');
  const [createdKey, setCreatedKey] = useState<ApiKeyCreatedResponse | null>(null);
  const [showOnceOpen, setShowOnceOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyResponse | null>(null);
  const [toast, setToast] = useState<Toast>(null);
  const { level, loaded, canRead, canWrite, openUpgrade } = useApiAccessLevel();

  const fetchKeys = useCallback(async () => {
    if (!loaded) return;
    if (!canRead) {
      setKeys([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try { setKeys(await apiKeysApi.list()); } catch (e) { console.warn('Failed to load API keys', e); } finally { setLoading(false); }
  }, [loaded, canRead]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!canWrite) { openUpgrade(); return; }
    try {
      const expiresAt = newExpiry ? new Date(Date.now() + parseInt(newExpiry) * 86400000).toISOString() : null;
      const result = await apiKeysApi.create({ name: newName, scopes: newScopes, expires_at: expiresAt });
      setCreatedKey(result);
      setCreateOpen(false);
      setShowOnceOpen(true);
      setNewName(''); setNewScopes([]); setNewExpiry('');
      fetchKeys();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:api.createFailed'), severity: 'error' });
    }
  };

  const handleRevoke = async () => {
    if (!canWrite) { openUpgrade(); return; }
    if (!revokeTarget) return;
    try {
      await apiKeysApi.revoke(revokeTarget.id);
      setToast({ message: t('integrations:api.keyRevoked', { name: revokeTarget.name }), severity: 'success' });
      setRevokeTarget(null);
      fetchKeys();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:api.revokeFailed'), severity: 'error' });
    }
  };

  const handleDelete = async (key: ApiKeyResponse) => {
    if (!canWrite) { openUpgrade(); return; }
    try {
      await apiKeysApi.delete(key.id);
      setToast({ message: t('integrations:api.keyDeleted', { name: key.name }), severity: 'success' });
      fetchKeys();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:api.deleteFailed'), severity: 'error' });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setToast({ message: t('integrations:api.copied'), severity: 'info' });
  };

  const activeKeys = keys.filter(k => k.status === 'active').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('integrations:api.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('integrations:api.subtitleDetail')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}><IconButton size="small" onClick={fetchKeys} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          <Button variant="contained" size="small" startIcon={canWrite ? <AddIcon /> : <LockOutlined />}
            onClick={() => (canWrite ? setCreateOpen(true) : openUpgrade())}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('integrations:api.createKey')}</Button>
          {/* HANDOFF_2026-05-25 Bug 25 — link goes to the public-facing
              SMB-friendly docs page on svdy.com, NOT the internal
              swagger at api.svdy.com/docs (password-gated, full
              endpoint surface, not for tenant consumption). */}
          <Button variant="outlined" size="small" startIcon={<OpenInNewOutlined />}
            component="a"
            href="https://svdy.com/integrations/api-keys"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)', bgcolor: '#ffffff', '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' } }}>
            {t('integrations:api.viewDocs')}
          </Button>
        </Stack>
      </Box>

      {/* Inline Stats */}
      {loaded && canRead && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('integrations:api.statsLine', {
            total: keys.length,
            active: activeKeys,
            revoked: keys.length - activeKeys,
          })}
        </Typography>
      )}

      {/* Capability is resolved once by useApiAccessLevel and shared with the banner. */}
      <PlanFeatureBanner level={level} loaded={loaded} requires="write" />

      {/* Keys Table */}
      {!loaded || loading ? (
        <Typography variant="body2" color="text.secondary">{t('common:status.loading')}</Typography>
      ) : !canRead ? null : keys.length === 0 ? (
        <Paper sx={{ p: 4, width: '100%', textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Typography variant="body2" color="text.secondary">{t('integrations:api.noKeys')}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.8rem', py: 0.75 } }}>
              <TableRow>
                <TableCell>{t('integrations:api.colName', { defaultValue: 'Name' })}</TableCell>
                <TableCell>{t('integrations:api.colKey', { defaultValue: 'Key' })}</TableCell>
                <TableCell>{t('integrations:api.colScopes', { defaultValue: 'Scopes' })}</TableCell>
                <TableCell>{t('integrations:api.colLastUsed', { defaultValue: 'Last Used' })}</TableCell>
                <TableCell align="center">{t('integrations:api.colCalls30d', { defaultValue: 'Calls (30d)' })}</TableCell>
                <TableCell align="center">{t('integrations:api.colStatus', { defaultValue: 'Status' })}</TableCell>
                <TableCell align="center">{t('integrations:api.colActions', { defaultValue: 'Actions' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map(k => (
                <TableRow key={k.id} hover sx={{ opacity: k.status === 'revoked' ? 0.5 : 1 }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{k.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                      {t('integrations:api.createdOn', { date: k.created_at ? new Date(k.created_at).toLocaleDateString(activeLocale()) : '—' })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{k.key_prefix}••••••••</Typography>
                      <Tooltip title={t('integrations:api.copyPrefix')}><IconButton size="small" onClick={() => copyToClipboard(k.key_prefix)}><ContentCopyOutlined sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(k.scopes || []).slice(0, 2).map(s => <Chip key={s} label={s} size="small" variant="outlined" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />)}
                      {(k.scopes || []).length > 2 && <Chip label={`+${k.scopes!.length - 2}`} size="small" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />}
                      {(k.scopes || []).length === 0 && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{t('integrations:api.fullAccess')}</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{k.last_used_at ? new Date(k.last_used_at).toLocaleString(activeLocale()) : t('integrations:api.never')}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="caption" sx={{ fontWeight: 600 }}>{(k.calls_30d || 0).toLocaleString(activeLocale())}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip label={k.status === 'active' ? t('common:status.active') : t('integrations:api.revoked')} size="small" variant="outlined"
                      color={k.status === 'active' ? 'success' : 'error'}
                      sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {k.status === 'active' && (
                        <Tooltip title={t('integrations:api.revokeTooltip')}><IconButton size="small" onClick={() => setRevokeTarget(k)} sx={{ '&:hover': { color: 'warning.main' } }}><BlockOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      )}
                      {k.status === 'revoked' && (
                        <Tooltip title={t('integrations:api.deleteTooltip')}><IconButton size="small" onClick={() => handleDelete(k)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {t('integrations:api.bearerTokenTip')}{' '}
        <Typography
          component="a"
          href="https://svdy.com/integrations/api-keys"
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{ color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
        >
          {t('integrations:api.setupGuide')}
        </Typography>
      </Typography>

      {/* Create Key Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('integrations:api.createApiKey', { defaultValue: 'Create API Key' })}</Typography>
          <IconButton size="small" onClick={() => setCreateOpen(false)}><CloseOutlined fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            <TextField label={t('integrations:api.keyNameLabel')} placeholder={t('integrations:api.keyNamePlaceholder')} value={newName}
              onChange={e => setNewName(e.target.value)} size="small" fullWidth required />
            <FormControl size="small" fullWidth>
              <InputLabel>{t('integrations:api.scopesLabel')}</InputLabel>
              <Select multiple value={newScopes} onChange={e => setNewScopes(e.target.value as string[])}
                input={<OutlinedInput label={t('integrations:api.scopesLabel')} />}
                renderValue={sel => sel.join(', ')}>
                {AVAILABLE_SCOPES.map(s => (
                  <MenuItem key={s} value={s}><Checkbox size="small" checked={newScopes.includes(s)} /><ListItemText primary={s} primaryTypographyProps={{ fontSize: '0.8rem' }} /></MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('integrations:api.expirationLabel')}</InputLabel>
              <Select value={newExpiry} onChange={e => setNewExpiry(e.target.value)} label={t('integrations:api.expirationLabel')}>
                {EXPIRY_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{t(o.i18nKey)}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {t('integrations:api.keyOnceWarning')}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} size="small" sx={{ borderRadius: 1, textTransform: 'none' }}>{t('integrations:api.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} size="small" disabled={!newName.trim()}
            sx={{ borderRadius: 1, textTransform: 'none' }}>{t('integrations:api.createKey')}</Button>
        </DialogActions>
      </Dialog>

      {/* Show-Once Dialog */}
      <Dialog open={showOnceOpen} onClose={() => setShowOnceOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #2e7d32, #2e7d32dd)', color: '#fff', position: 'relative' }}>
          <IconButton size="small" onClick={() => setShowOnceOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, color: 'inherit', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <CloseOutlined fontSize="small" />
          </IconButton>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}>
              <ContentCopyOutlined />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{t('integrations:api.keyCreated')}</Typography>
              <Chip label={createdKey?.name} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit', height: 22, fontSize: '0.7rem', mt: 0.5 }} />
            </Box>
          </Stack>
        </Box>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{t('integrations:api.yourApiKey')}</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', fontWeight: 600 }}>
              {createdKey?.key}
            </Typography>
          </Box>
          <Button size="small" startIcon={<ContentCopyOutlined />} onClick={() => createdKey && copyToClipboard(createdKey.key)}
            sx={{ borderRadius: 1, textTransform: 'none', mb: 2 }}>{t('integrations:api.copyToClipboard')}</Button>
          <Box sx={{ p: 1.5, bgcolor: 'warning.50', borderRadius: 1.5, border: '1px solid', borderColor: 'warning.200' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <WarningAmberOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'warning.dark' }}>
                {t('integrations:api.keyOnlyOnce')}
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button variant="contained" onClick={() => setShowOnceOpen(false)} size="small"
            sx={{ borderRadius: 1, textTransform: 'none' }}>{t('integrations:api.savedKey')}</Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirm Dialog */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('integrations:api.revokeApiKey', { defaultValue: 'Revoke API Key' })}</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            {t('integrations:api.revokeConfirm', { name: revokeTarget?.name ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setRevokeTarget(null)} size="small" sx={{ borderRadius: 1, textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleRevoke} size="small"
            sx={{ borderRadius: 1, textTransform: 'none' }}>{t('integrations:api.revokeKey', { defaultValue: 'Revoke Key' })}</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default API;
